import { type ReactElement, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

interface ProtectedRouteProps {
  children: ReactElement;
}

/**
 * ProtectedRoute - wrapper komponent pre chránené routes
 * Kontroluje autentifikáciu a presmeruje na /login ak používateľ nie je prihlásený
 * Nerenderuje children, kým nie je používateľ prihlásený
 */
export function ProtectedRoute({
  children,
}: ProtectedRouteProps): ReactElement | null {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Ak nie je prihlásený po dokončení loading, presmerovať na login
    // Používame navigate namiesto window.location.href, aby sa zabránilo full page reload
    if (!isLoading && !user) {
      navigate("/login", { replace: true });
    }
  }, [user, isLoading, navigate]);

  // Show loading spinner while loading
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto"></div>
          <p className="mt-4 text-sm text-muted-foreground">
            Kontrolujem prihlasovanie...
          </p>
        </div>
      </div>
    );
  }

  // Show nothing if no user (redirect will happen)
  if (!user) {
    return null;
  }

  // Render children only when user is authenticated
  return children;
}
